import {
  Button,
  Checkbox,
  DialogActions,
  DialogContent,
  styled,
  TextField,
  Typography,
  Dialog,
  duration,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useState } from "react";
import { RRule } from "rrule";
import { fromZonedTime, toZonedTime } from "date-fns-tz";

import { allDay, defaultWorkday, isAllDay, Availability, getAvailabilityLabel } from "@caredna-marlin/utils/client/availability";
import RepeatDailyForm from "./RepeatDailyForm";
import RepeatWeeklyForm from "./RepeatWeeklyForm";
import StartEndTime from "~/components/common/forms/StartEndTime";

import { blockFieldInfo } from "@caredna-marlin/utils/calendarBlockHelper";
import EditAvailabilityAvailable from "./EditAvailabilityAvailable";
import EditAvailabilityBlock from "./EditAvailabilityBlock";
import EditAvailabilityOccurs from "./EditAvailabilityOccurs";
import BulkOperation from "./BulkOperation";
import { startCase } from "lodash";
import useAvailabilityOperation, { AvailabilityAction } from "~/hooks/useAvailabilityOperation";
import { differenceInDays, startOfToday } from "date-fns";
import { AvailabilityByTimespanQuery, AvailTzHandling } from "~/graphql/__generated__/graphql";
import SaveWithTimezoneHandling from "./SaveWithTimezoneHandling";
import { useOrgConfig } from "~/providers/OrgConfigProvider";
import { getRRuleGenerationDays } from "~/utils/providers";

type Props = {
  availability: Availability,
  action: AvailabilityAction,
  manualDays: Record<string, { providerIds: number[], availabilityIds: number[] }>,
  onClose: () => void,
  provider: AvailabilityByTimespanQuery["provider"][number],
  selectedDate: Date,
}

const EditAvailability = ({ availability, action, manualDays, onClose, provider, selectedDate }: Props) => {
  // An edited copy of the availability, which can be discarded on "Cancel"
  // clampStartEnd also forces rrule.dtstart >= tomorrow,
  // meaning the UI will only let them save availability starting in the future.
  const [edited, setEdited] = useState(() => ({...availability, rrule: {...availability.rrule}}));

  const { serviceLines, visitTypes } = useOrgConfig()
  const [bulkEditExpanded, setBulkEditExpanded] = useState(action === "copy" || action === "delete");
  const [additionalProviders, setAdditionalProviders] = useState<Record<string, boolean>>({});
  const [additionalAvailabilityIds, setAdditionalAvailabilityIds] = useState<number[]>([]);
  const additionalProvidersSelected = Object.keys(additionalProviders).filter(id => additionalProviders[id]).map(id => parseInt(id))

  const { onSave: updateAvailability, loading } = useAvailabilityOperation();

  const hideStartEnd = edited.type === "unavailable" && isAllDay(edited);

  const onSave = async (tzOption: AvailTzHandling, providerTzs: Record<string, string>) => {
    await updateAvailability(action, edited, additionalProvidersSelected, additionalAvailabilityIds, tzOption, providerTzs);
    onClose();
  }
  const info = blockFieldInfo(edited.blockType);

  const startLocked = availability.rrule.dtstart < startOfToday();
  const disableForm = action === "delete";
  const needsAddress = info.requiredAddress && !edited.addressCity;
  const needsVisitTypes = info.shouldRequireVisitTypes && !edited.blockFilters?.visitTypeIds?.length;
  const needsAdditionalProviders = action === "copy" && !additionalProvidersSelected.length;

  const providerName = `${provider?.firstName} ${provider?.lastName}`;
  const providerTimezone = provider?.addressTimezone || "";

  let toHelpText = undefined
  const daysAdvance = getRRuleGenerationDays(provider, serviceLines, visitTypes)
  if (!edited.rrule.until || differenceInDays(new Date(), edited.rrule.until) > daysAdvance) {
    toHelpText = `Events will only show up on the calender for ${providerName} ${daysAdvance} days in advance, because this is the scheduling maximum for visit types they can work`;
  }

  return <Dialog open={true} onClose={onClose} maxWidth={false}>
    <StyledDialogContent sx={{
      transition: `width ${duration.standard}ms ease-in-out`,
      width: bulkEditExpanded ? "min(95vw, 1600px)" : "min(90vw, 600px)",
      maxWidth: "none",
    }}>
      <Grid container spacing={2} sx={{ margin: "auto", maxWidth: 600, mb: 2 }}>
        <Grid size={12}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {startCase(action)} {getAvailabilityLabel(edited)} {action === "edit" ? "-" : "from"} {providerName}
          </Typography>
        </Grid>
        {edited.type === "unavailable" && <>
          <Grid size={3}><span>All Day</span></Grid>
          <Grid size={9}>
            <Checkbox
              checked={isAllDay(edited)}
              disabled={disableForm}
              onChange={(_, checked) => setEdited({
                ...edited,
                ...(checked ? allDay : defaultWorkday),
              })}
              sx={{ p: 0 }}
            />
          </Grid>
        </>}

        {!hideStartEnd && <>
          <Grid size={3}><span>Time</span></Grid>
          <Grid size={9}>
            <StartEndTime
              mode="time"
              disabled={disableForm}
              startTime={fromZonedTime(edited.localStartTime, providerTimezone)}
              endTime={fromZonedTime(edited.localEndTime, providerTimezone)}
              onChange={(start, end) => setEdited({
                ...edited,
                localStartTime: toZonedTime(start, providerTimezone),
                localEndTime: toZonedTime(end, providerTimezone),
              })}
              timezone={providerTimezone ?? undefined}
            />
          </Grid>
        </>}

        <Grid size={3}><span>Title</span></Grid>
        <Grid size={9}>
          <TextField
            value={edited.note}
            sx={{ width: "100%" }}
            disabled={disableForm}
            onChange={(e) => setEdited({ ...edited, note: e.target.value })}
          />
        </Grid>

        {edited.type === "available" && <EditAvailabilityAvailable edited={edited} setEdited={setEdited} disabled={disableForm} />}
        {edited.type === "calendar_block" && <EditAvailabilityBlock edited={edited} setEdited={setEdited} disabled={disableForm} />}

        <EditAvailabilityOccurs edited={edited} manualDays={manualDays} selectedDate={selectedDate} setEdited={setEdited} disabled={disableForm} />

        {edited.rrule.freq === RRule.DAILY ?
          <RepeatDailyForm rrule={edited.rrule} setRRule={rrule => setEdited({ ...edited, rrule })} disabled={disableForm} startLocked={startLocked} toHelpText={toHelpText} />
        :
          <RepeatWeeklyForm rrule={edited.rrule} setRRule={rrule => setEdited({ ...edited, rrule })} disabled={disableForm} startLocked={startLocked} untilHelpText={toHelpText} />
        }
      </Grid>

      <BulkOperation
        action={action}
        availability={availability}
        additionalProviders={additionalProviders}
        expanded={bulkEditExpanded}
        setAdditionalProviders={setAdditionalProviders}
        setExpanded={disableForm ? undefined : setBulkEditExpanded}
        setAdditionalAvailabilityIds={setAdditionalAvailabilityIds}
      />
    </StyledDialogContent>
    <DialogActions>
      <Button disabled={loading} onClick={onClose}>
        Cancel
      </Button>
      <SaveWithTimezoneHandling
        availability={edited}
        additionalProviders={additionalProviders}
        action={action}
        onSave={onSave}
        loading={loading}
        disabled={needsAddress || needsVisitTypes || needsAdditionalProviders}
      />
    </DialogActions>
  </Dialog>
}

export default EditAvailability;

const StyledDialogContent = styled(DialogContent)`
  .MuiOutlinedInput-input {
    padding: 6px;
  }

  .MuiGrid-item {
    display: table;
  }

  .MuiGrid-item > span {
    display: table-cell;
    vertical-align: middle;
  }
`
